import api from '@/lib/axios'
import { getMediaUrl } from '@/utils/url-utils'

export interface Category {
    id: string
    _id?: string
    name: string | LocalizedString
    slug: string | LocalizedString
    description?: string | LocalizedString
    parentIds?: string[]
    parentId?: string
    imageUrl?: string
    productCount?: number
    seo?: {
        meta_title?: LocalizedString
        meta_description?: LocalizedString
        meta_keywords?: LocalizedString
    }
}

export interface GalleryImage {
    imageUrl: string
    altText?: string
}

export interface LocalizedString {
    tr: string
    [key: string]: string
}

export interface Product {
    id: string
    _id?: string
    title: string | LocalizedString
    slug: string | LocalizedString
    description: string | LocalizedString
    categoryIds: string[]
    externalUrl?: string
    modelUrl?: string
    isActive?: boolean
    orderIndex?: number
    gallery?: GalleryImage[]
    details?: string | LocalizedString
    specs?: Record<string, unknown>
    seo?: {
        meta_title?: LocalizedString
        meta_description?: LocalizedString
        meta_keywords?: LocalizedString
    }
    createdAt?: string
    updatedAt?: string
}

export const productService = {
    // Categories
    getCategories: async () => {
        const res = await api.get<Category[]>('/products/categories', { params: { lang: 'all' } })
        return res.data.map(c => ({
            ...c,
            imageUrl: getMediaUrl(c.imageUrl)
        }))
    },
    getCategoryById: async (id: string) => {
        const res = await api.get<Category>(`/products/categories/${id}`, { params: { lang: 'all' } })
        return {
            ...res.data,
            imageUrl: getMediaUrl(res.data.imageUrl)
        }
    },
    createCategory: async (data: Partial<Category>) => {
        const res = await api.post<Category>('/products/categories', data)
        return {
            ...res.data,
            imageUrl: getMediaUrl(res.data.imageUrl)
        }
    },
    updateCategory: async (id: string, data: Partial<Category>) => {
        const res = await api.put<Category>(`/products/categories/${id}`, data)
        return {
            ...res.data,
            imageUrl: getMediaUrl(res.data.imageUrl)
        }
    },
    deleteCategory: async (id: string) => {
        const res = await api.delete(`/products/categories/${id}`)
        return res.data
    },
    bulkTranslateCategories: async () => {
        const res = await api.post('/products/categories/bulk-translate')
        return res.data
    },

    // Products
    getProducts: async (params?: { page?: number; limit?: number; categoryIds?: string; lang?: string }) => {
        const res = await api.get<{ products: any[]; pagination: { total: number } }>('/products', { params: { lang: 'all', ...params } })
        return {
            items: res.data.products.map(p => ({
                ...p,
                id: p._id || p.id,
                categoryIds: p.categoryIds?.map((c: any) => typeof c === 'object' ? c._id || c.id : c) || [],
                gallery: p.gallery?.map((g: any) => ({
                    ...g,
                    imageUrl: getMediaUrl(g.imageUrl)
                })),
                modelUrl: getMediaUrl(p.modelUrl)
            })),
            total: res.data.pagination.total
        }
    },
    getProductBySlug: async (slug: string, lang: string = 'all') => {
        const res = await api.get<any>(`/products/${slug}`, { params: { lang } })
        const p = res.data
        return {
            ...p,
            id: p._id || p.id,
            gallery: p.gallery?.map((g: any) => ({
                ...g,
                imageUrl: getMediaUrl(g.imageUrl)
            })),
            modelUrl: getMediaUrl(p.modelUrl)
        }
    },
    createProduct: async (data: Partial<Product>) => {
        const res = await api.post<Product>('/products', data)
        return res.data
    },
    updateProduct: async (id: string, data: Partial<Product>) => {
        const res = await api.put<Product>(`/products/${id}`, data)
        return res.data
    },
    deleteProduct: async (id: string) => {
        const res = await api.delete(`/products/${id}`)
        return res.data
    },
    bulkTranslateProducts: async () => {
        const res = await api.post('/products/bulk-translate')
        return res.data
    },

    // File Uploads
    uploadProductImages: async (files: File[]) => {
        const formData = new FormData()
        files.forEach(file => formData.append('images', file))
        const res = await api.post<{ images: { url: string }[] }>('/upload/product-images', formData)
        return {
            ...res.data,
            images: res.data.images.map(img => ({
                ...img,
                url: getMediaUrl(img.url)
            }))
        }
    },
    uploadCategoryImage: async (file: File) => {
        const formData = new FormData()
        formData.append('images', file)
        const res = await api.post<{ images: { url: string }[] }>('/upload/product-images', formData)
        const firstImg = res.data.images[0]
        return {
            url: getMediaUrl(firstImg?.url)
        }
    },
    uploadProductModel: async (file: File) => {
        const formData = new FormData()
        formData.append('model', file)
        const res = await api.post<{ model: { url: string } }>('/upload/product-model', formData)
        return {
            ...res.data,
            model: {
                ...res.data.model,
                url: getMediaUrl(res.data.model.url)
            }
        }
    }
}
